import get from 'lodash/get';

import { getContextCountry } from 'Store/context/selectors';
import {
  getHasSubscription,
  getLikesRemaining,
  getSuperLikesRemaining,
} from 'Store/profile/revenue.selectors';
import { getCartProductId } from 'Store/purchase/selectors';

import { getSkuPart } from './lib/purchaseUtils';
import { TINDER_PLUS_RESTORE } from './sparks.events';
import SparksService from './SparksService';

// TinderPlus.Paywall From Field Values: https://gotinder.atlassian.net/wiki/x/1JCQAQ
// Plus Paywall
export const PAYWALL_FROM_UNDO = 0;
export const PAYWALL_FROM_PASSPORT_ADD_LOCATION = 1;
export const PAYWALL_FROM_PASSPORT_RECENT_LOCATION = 2;
export const PAYWALL_FROM_BOUNCER_RATE_LIMITING = 3;
export const PAYWALL_FROM_SUPER_LIKE = 4;
export const PAYWALL_FROM_MENU = 5;
export const PAYWALL_FROM_RECS_BLEND = 6;
export const PAYWALL_FROM_ATTEMPTING_TO_HIDE_ADVERTISEMENTS = 7;
export const PAYWALL_FROM_GENERAL_SETTING_SCREEN = 8;
export const PAYWALL_FROM_USER_PROFILE_SETTINGS_SCREEN = 9;
export const PAYWALL_FROM_MERCHANDISING_FEATURES_PAGE = 10;
export const PAYWALL_FROM_MERCHANDISING_FEATURES_TOP_BUTTON = 11;
export const PAYWALL_FROM_MERCHANDISING_FEATURES_BOTTOM_BUTTON = 12;
export const PAYWALL_FROM_AUTO_OPEN = 14;
export const PAYWALL_FROM_DISCOUNTING_AUTO_REMINDER = 15;
export const PAYWALL_FROM_END_OF_BOOST_SESSION = 16;
export const PAYWALL_FROM_PUSH_NOTIFICATION = 17;
export const PAYWALL_FROM_BOOST = 18;
export const PAYWALL_FROM_BOOST_IN_PROGRESS_MODAL = 19;
export const PAYWALL_FROM_SUPER_LIKE_CONSUMABLES = 20;
export const PAYWALL_FROM_LIMITED_TIME_OFFER = 21;
export const PAYWALL_FROM_MERCHANDISING_PANEL = 22;
export const PAYWALL_UNKNOWN = 23; // Default value when the correct source of the paywall is unknown
export const PAYWALL_FROM_SWIPE_NOTE_EXHAUSTED = 13;
export const PAYWALL_FROM_BOUNCER_BYPASS = 37;
export const PAYWALL_FROM_NEW_MERCHANDISING_PAGE = 65;
export const PAYWALL_FROM_PREFERENCES = 68;
export const PAYWALL_FROM_WEB_DISCOUNT_BANNER = 102;
export const PLUS_PAYWALL_VERSION = 4;
export const PLUS_PAYWALL_FROM_MATCHLIST_BANNER = 93;

// Tinder gold paywall options
// https://github.com/TinderBackend/etl_schemas/tree/master/docs#goldpaywall
export const GOLD_PAYWALL_FROM_LIKES_YOU_ENTRY_POINT = 0;
export const GOLD_PAYWALL_FROM_GENERAL_SETTING_SCREEN = 1;
export const GOLD_PAYWALL_FROM_DEEPLINK = 3;
export const GOLD_PAYWALL_FROM_CONTROLLA_FASTMATCH_FEATURE = 4;
export const GOLD_PAYWALL_FROM_MERCHANDISING_FEATURES_PAGE = 5;
export const GOLD_PAYWALL_FROM_BOUNCER = 10;
export const GOLD_PAYWALL_FROM_CANCEL_FLOW = 83;
// Gold home
export const GOLD_PAYWALL_FROM_GOLD_HOME_TAP = 40;
export const GOLD_PAYWALL_FROM_GOLD_HOME_SCROLL = 42;
export const GOLD_PAYWALL_FROM_GOLD_HOME_BUTTON = 46;
export const GOLD_PAYWALL_FROM_GOLD_HOME_BUTTON_EMPTY = 52;
export const GOLD_PAYWALL_FROM_NEW_LIKES_MODAL = 54;
export const GOLD_PAYWALL_FROM_RECENTLY_ACTIVE_MESSAGE = 57;
export const GOLD_PAYWALL_FROM_GOLD_HOME_FILTERS = 62;
export const GOLD_PAYWALL_FROM_MATCHLIST_BANNER = 73;

export const GOLD_INTRO_OFFER_TEMPLATE_V2_PAYWALL_VERSION = 9;
export const GOLD_INTRO_OFFER_TEMPLATE_V2_WITH_PERSONALIZED_OFFER_PAYWALL_VERSION = 10;

// https://github.com/TinderBackend/etl_schemas/tree/master/docs#superlikepaywall
export const SUPERLIKE_PAYWALL_FROM_EXHAUSTED = 6;
export const SUPERLIKE_PAYWALL_FROM_PROFILE_SETTINGS = 7;
export const SUPERLIKE_PAYWALL_FROM_MERCHANDISING_PANEL = 8;
export const SUPERLIKE_PAYWALL_FROM_HIGH_RSRR_UPSELL = 34;
export const SUPERLIKE_PAYWALL_FROM_MY_LIKES_EXHAUSTED = 35;

// https://github.com/TinderBackend/etl_schemas/tree/master/docs#boostpaywall
// Boost
export const BOOST_PAYWALL_FROM_EXHAUSTED = 6;
export const BOOST_PAYWALL_FROM_PROFILE_SETTINGS = 7;
export const BOOST_PAYWALL_FROM_MERCHANDISING_PANEL = 8;
export const BOOST_PAYWALL_SUPERBOOST_VERSION = 3;
export const BOOST_PAYWALL_VERSION = 2;
export const BOOST_PAYWALL_FROM_MERCHANDISING_CARD = 25;
export const BOOST_PAYWALL_FROM_OUT_OF_RECS_UPSELL = 28;
export const BOOST_PAYWALL_FROM_GET_LIKES_UPSELL = 29;

// Primetime Boost
export const PRIMETIME_BOOST_PAYWALL_VERSION_CAROUSEL_UPSELL = 6;
export const PRIMETIME_BOOST_PAYWALL_FROM_CAROUSEL_UPSELL = 72;

// Superboost
export const SUPERBOOST_PAYWALL_FROM_SUPERBOOST_AGAIN = 9;
export const SUPERBOOST_PAYWALL_VERSION = 4;
export const SUPERBOOST_PAYWALL_FROM_GOLD_HOME_BUTTON = 25;

// Boost Source
export const BOOST_SOURCE_GAMEPAD = 0;
export const BOOST_SOURCE_FAST_MATCH = 1;
export const BOOST_SOURCE_GOLD_HOME = 3;
export const BOOST_SOURCE_MERCH_CARD = 7;
export const BOOST_SOURCE_BOOST_UPSELL = 8;
export const BOOST_SOURCE_EXPLORE = 9;

// Read receipts
export const READ_RECEIPT_PAYWALL_FROM_EMPTY_MATCH_SCREEN = 0;
export const READ_RECEIPT_PAYWALL_FROM_CHAT = 1;

export const INTRO_PRICE_CAMPAIGN_TEST_NAME = 'intro_pricing_v1';

// Swipenote
export const SUPERLIKE_PAYWALL_FROM_A_LACARTE = 3;
export const SUPERLIKE_PAYWALL_FROM_SWIPENOTE_EXHAUSTED = 6;

// Platinum
export const PLATINUM_PAYWALL_VERSION = 1.0;
export const PLATINUM_PAYWALL_FROM_CONTROLLA = 0;
export const PLATINUM_PAYWALL_FROM_SETTINGS = 1;
export const PLATINUM_PAYWALL_FROM_SWIPE_NOTE = 4;
export const PLATINUM_PAYWALL_FROM_PRIORITY_LIKES_MODAL = 5;
export const PLATINUM_PAYWALL_FROM_BOUNCER = 6;
export const PLATINUM_PAYWALL_FROM_MYLIKES = 8;
export const PLATINUM_PAYWALL_FROM_PRIORITY_LIKES_BANNER = 13;
export const PLATINUM_PAYWALL_FROM_MYLIKES_SWIPENOTE = 15;
export const PLATINUM_PAYWALL_FROM_AUTO_OPEN = 18;
export const PLATINUM_PAYWALL_FROM_REMINDER_BANNER = 22;
export const PLATINUM_PAYWALL_FROM_MATCHLIST_BANNER = 83;

export const PLATINUM_INTRO_OFFER_TEMPLATE_V2_PAYWALL_VERSION = 10;
export const PLATINUM_INTRO_OFFER_TEMPLATE_V2_WITH_PERSONALIZED_OFFER_PAYWALL_VERSION = 11;

// Offer modals
export const CONSUMABLE_PAYWALL_FROM_OFFER_MODAL_ENTRY_POINT = 27;

// Upsell modals
export const BOOST_FROM_GOLD_HOME_UPSELL = 26;
export const MY_LIKES_UPSELL_FROM_ENTRYPOINT = 11;
export const MY_LIKES_UPSELL_FROM_BOTTOM_SCROLL = 12;

// Bouncer ALC
export const BOUNCER_BYPASS_FROM_OUT_OF_LIKES = 0;

// Merchandising page
export const MERCH_PAGE_FROM_SETTINGS = 0;
export const MERCH_PAGE_FROM_CONTROLLA = 1;

// Dyno Paywalls
export const DYNO_CAROUSEL_B_PAYWALL_VERSION = 7;
export const DYNO_CAROUSEL_SUB_PAYWALL_VERSION = 8;
export const DYNO_CAROUSEL_B_FROM_RECS = 1;
export const DYNO_CAROUSEL_B_FROM_SETTINGS = 4;
export const DYNO_CAROUSEL_B_FROM_LIKES_YOU = 7;
export const DYNO_CAROUSEL_B_FROM_GOLD_HOME = 23;
export const DYNO_CAROUSEL_B_FROM_UPSELL = 72;
export const DYNO_CAROUSEL_B_FROM_Y_FOR_X_BOOST = 30;
export const DYNO_CAROUSEL_SUB_PAYWALL_FROM_TINDER_SELECT = 82;

// Boost Sub
export const BOOST_SUB_PAYWALL_VERSION = 1;

// Boost Wallet
export const BOOST_WALLET_PAYWALL_FROM_CONTROLLA = 22;

// Y for X Boost
export const Y_FOR_X_BOOST_LOCATION_GAMEPAD = 'gamepad';
export const Y_FOR_X_BOOST_LOCATION_FAST_MATCH = 'fastmatch';
export const Y_FOR_X_BOOST_LOCATION_GOLD_HOME = 'gold_home';
export const Y_FOR_X_BOOST_LOCATION_MERCH_CARD = 'merch_card';
export const Y_FOR_X_BOOST_LOCATION_BOOST_UPSELL = 'boost_upsell';
export const Y_FOR_X_BOOST_LOCATION_EXPLORE = 'curated_card_stack';

// Incognito Mode
export const PAYWALL_FROM_INCOGNITO = 36;
export const GOLD_PAYWALL_FROM_INCOGNITO = 74;

const PAYWALL_EVENTS_KEYS = {
  [TINDER_PLUS_RESTORE]: [
    'basePrice',
    'currency',
    // 'discountCampaign',
    // 'discountedPrice',
    // 'discountTestGroup',
    'features',
    // 'incentives',
    // 'incentivesOrdering',
    'from',
    'paywallVersion',
    'price',
    // 'purchaseCodeVersion',
    // 'roadblockVersion',
    'sku',
    'success',
    'superLikesRemaining',
    'term',
    'testName',
  ],
};

class PaywallSparks {
  getStore = () => SparksService._store;

  mapState = (state) => ({
    country: getContextCountry(state) || 'US',
    likesRemaining: getLikesRemaining(state),
    superLikesRemaining: getSuperLikesRemaining(state),
    hasSubscription: getHasSubscription(state),
    comittedProductId: getCartProductId(state),
  });

  getSparksEvents = (state, sparksEvents, eventData = {}) => {
    const { country, likesRemaining, superLikesRemaining, hasSubscription, comittedProductId } =
      this.mapState(state);

    const productId = eventData.productId || comittedProductId;

    const {
      basePrice,
      discountCampaign,
      discountTestGroup,
      featuresList,
      from,
      feature,
      products,
      purchaseID,
      testName,
    } = eventData;

    const currency = get(products, [productId, 'currency']);
    const taxRate = get(products, [productId, 'taxRate']);
    const amount = get(products, [productId, 'amount']);
    const price = get(products, [productId, 'price']);

    return sparksEvents.reduce((acc, event) => {
      switch (event) {
        case 'basePrice':
          if (basePrice) {
            acc[event] = basePrice;
          } else if (productId) {
            acc[event] = parseInt(getSkuPart(productId, 2), 10) / 100;
          }
          break;

        case 'currency':
          if (currency) {
            acc[event] = currency;
          }
          break;

        case 'feature':
          if (feature) {
            acc[event] = feature;
          }
          break;
        case 'features':
          if (featuresList) {
            acc[event] = featuresList;
          }
          break;

        case 'locale':
          acc[event] = country;
          break;

        case 'from':
          if (from !== null) {
            acc[event] = from;
          }
          break;

        case 'percentLikesLeft':
          acc[event] = hasSubscription ? 100 : likesRemaining;
          break;

        case 'price':
          if (products && productId) {
            acc[event] = price;
          }
          break;

        case 'products':
          if (products) {
            acc[event] = Object.keys(products).map((id) => ({
              sku: products[id].productId,
              locale: country,
              price: products[id].price,
            }));
          }
          break;

        case 'purchaseID':
          if (purchaseID) {
            acc[event] = purchaseID;
          }
          break;

        case 'sku':
          if (productId) {
            acc[event] = productId;
          }
          break;

        case 'skus':
          if (products) {
            acc[event] = Object.keys(products);
          }
          break;

        case 'superLikesRemaining':
          acc[event] = superLikesRemaining;
          break;

        case 'term': {
          if (productId) {
            const term = getSkuPart(productId, 3);
            if (!term || !term.endsWith('m')) {
              break;
            }
            acc[event] = parseInt(term.substring(0, term.length - 1), 10);
          }
          break;
        }

        case 'unlimitedLikesOffered':
          acc[event] = false;
          break;

        case 'taxRate':
          acc[event] = taxRate;
          break;

        case 'amount':
          acc[event] = amount || 1;
          break;
        case 'discountCampaign':
          if (discountCampaign) {
            acc[event] = discountCampaign;
          }
          break;
        case 'discountedPrice':
          if (discountCampaign && products && productId) {
            acc[event] = price;
          }
          break;
        case 'discountTestGroup':
          if (discountTestGroup) {
            acc[event] = discountTestGroup;
          }
          break;
        case 'testName':
          if (testName) {
            acc[event] = testName;
          }
          break;
        default:
          break;
      }
      return acc;
    }, {});
  };

  getEventData = (state, eventName, uncomittedProductId) =>
    this.getSparksEvents(state, PAYWALL_EVENTS_KEYS[eventName], uncomittedProductId);
}

export default new PaywallSparks();
